<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <h1 class="title">Rental companies</h1>
      <app-cells>
        <app-button v-if="is_transfer_supervisor" @click.native="$modal.show('company-form')">
          Add rental company
        </app-button>
        <app-input
            @input.native="onUsersSearch"
            v-model="search"
            class="input--200"
            type="search"
            placeholder="Search by name"
            name="search"
        />
      </app-cells>
      <v-client-table
          :data="table.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #name="{ row }">
          <div class="table-default__left">
            <router-link :to="{ name: 'rental-companies-detail', params: { id: row.id } }" class="table-default__link-border">
              {{ row.name }}
            </router-link>
          </div>
        </template>
        <template #total_ordered="{ row }">{{ row.total_ordered.toLocaleString() }} TSh</template>
        <template #total_unpaid="{ row }">{{ row.total_unpaid.toLocaleString() }} TSh</template>
        <template #total_paid="{ row }">{{ row.total_paid.toLocaleString() }} TSh</template>
        <template #car_models="{ row }">
          <span v-tooltip="{ content: tooltipContent(row.car_models), placement: 'top-start', offset: 10, html: true }">
            {{ row.car_models.length }}
          </span>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
              :clickHandler="onClickPagination"
              v-if="pagination.pages > 1"
              v-model="pagination.page"
              :page-count="pagination.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
        name="company-form"
        :width="700"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('company-form')" class="popup__close" type="button"></button>
        <div class="popup__title">Adding rental company</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-grid>
              <template #item-1>
                <app-form-group label="Name" label-for="company_name" required>
                  <app-input
                      v-model="form.name"
                      @change.native="$v.form.name.$touch()"
                      :error="$v.form.name.$error"
                      id="company_name"
                      placeholder="Name"
                      name="company_name"
                  />
                  <template #error>
                    <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Car models" required>
                  <app-select
                    v-model="form.car_models"
                    :reduce="item => item.id"
                    :options="cars_options"
                    :clearable="false"
                    placeholder="Car models"
                    multiple
                    :error="$v.form.car_models.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.car_models.$dirty && !$v.form.car_models.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
            </app-grid>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Add rental company</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getTransfersRentalCompanyList, getCarManagementCarModels, postTransfersRentalCompany } from '@/http'
import debounce from 'lodash/debounce'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'RentalCompaniesList',
  data() {
    return {
      is_loaded: false,
      search: '',
      cars_options: [],
      form: {},
      table: {
        items: [],
        columns: ['name', 'total_ordered', 'total_unpaid', 'total_paid', 'car_models', 'cars_count', 'drivers_count'],
        options: {
          headings: {
            'name': 'Name',
            'total_ordered': 'Total ordered',
            'total_unpaid': 'Total unpaid',
            'total_paid': 'Total paid',
            'car_models': 'Car models',
            'cars_count': 'Cars',
            'drivers_count': 'Drivers',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false
    }
  },
  validations: {
    form: {
      name: { required },
      car_models: { required },
    }
  },
  computed: {
    is_transfer_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('transfer_supervisor')
    },
  },
  created() {
    this.fetchItems()
    getCarManagementCarModels().then(res => { this.cars_options = res.data })
  },
  methods: {
    fetchItems() {
      getTransfersRentalCompanyList(
        { page: this.pagination.page, page_size: 20, search: this.search }
      )
        .then(res => {
          this.table.items = res.data.results
          this.pagination.pages = res.data.pages
          this.pagination.count = res.data.count
          this.is_loaded = true
          this.table_preloader = false
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      postTransfersRentalCompany(this.form)
        .then(res => {
          this.$notify({
            type: 'success',
            title: 'Rental company created',
          })
          this.$router.push({ name: 'rental-companies-detail', params: { id: res.data.id } })
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onUsersSearch() {
      this.searchUsers(this)
    },
    searchUsers: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    tooltipContent(cars) {
      if (cars.length) {
        let string = ''

        cars.forEach(item => {
          string += `${item.name}<br>`
        })
        return string
      } else {
        return '-'
      }
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
  }
}
</script>
